<script setup>
import { computed, useSlots, useAttrs } from 'vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { buttonVariants } from 'next/polaris/ui/button';
import { cn } from 'next/polaris/lib/utils';
import {
  VARIANT_OPTIONS,
  COLOR_OPTIONS,
  JUSTIFY_OPTIONS,
  SIZE_OPTIONS,
  EXCLUDED_ATTRS,
} from './constants.js';

const props = defineProps({
  label: { type: [String, Number], default: '' },
  variant: {
    type: String,
    default: null,
    validator: value => VARIANT_OPTIONS.includes(value) || value === null,
  },
  color: {
    type: String,
    default: null,
    validator: value => COLOR_OPTIONS.includes(value) || value === null,
  },
  size: {
    type: String,
    default: null,
    validator: value => SIZE_OPTIONS.includes(value) || value === null,
  },
  justify: {
    type: String,
    default: null,
    validator: value => JUSTIFY_OPTIONS.includes(value) || value === null,
  },
  icon: { type: [String, Object, Function], default: '' },
  trailingIcon: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  noAnimation: { type: Boolean, default: false },
});

const slots = useSlots();
const attrs = useAttrs();

defineOptions({
  inheritAttrs: false,
});

const computedVariant = computed(() => {
  if (props.variant) return props.variant;
  // The useAttrs method returns attributes values an empty string (not boolean value as in props).
  if (attrs.solid || attrs.solid === '') return 'solid';
  if (attrs.outline || attrs.outline === '') return 'outline';
  if (attrs.faded || attrs.faded === '') return 'faded';
  if (attrs.link || attrs.link === '') return 'link';
  if (attrs.ghost || attrs.ghost === '') return 'ghost';
  return 'solid'; // Default variant
});

const computedColor = computed(() => {
  if (props.color) return props.color;
  if (attrs.blue || attrs.blue === '') return 'blue';
  if (attrs.ruby || attrs.ruby === '') return 'ruby';
  if (attrs.amber || attrs.amber === '') return 'amber';
  if (attrs.slate || attrs.slate === '') return 'slate';
  if (attrs.teal || attrs.teal === '') return 'teal';
  return 'blue'; // Default color
});

const computedSize = computed(() => {
  if (props.size) return props.size;
  if (attrs.xs || attrs.xs === '') return 'xs';
  if (attrs.sm || attrs.sm === '') return 'sm';
  if (attrs.md || attrs.md === '') return 'md';
  if (attrs.lg || attrs.lg === '') return 'lg';
  return 'md';
});

const computedJustify = computed(() => {
  if (props.justify) return props.justify;
  if (attrs.start || attrs.start === '') return 'start';
  if (attrs.center || attrs.center === '') return 'center';
  if (attrs.end || attrs.end === '') return 'end';

  return 'center';
});

const filteredAttrs = computed(() => {
  const standardAttrs = {};

  Object.entries(attrs)
    .filter(([key]) => !EXCLUDED_ATTRS.includes(key) && key !== 'class')
    .forEach(([key, value]) => {
      standardAttrs[key] = value;
    });

  return standardAttrs;
});

// The woot's variant + color, in the kit's words (fase 2 spec, item 2). The primary action is the kit's `default`
// (carbon in light, solar in dark); amber and teal have no kit button, so they are primary too.
const kitVariant = computed(() => {
  const variant = computedVariant.value;
  if (variant === 'link' || variant === 'ghost' || variant === 'outline') {
    return variant;
  }
  if (computedColor.value === 'ruby') return 'destructive';
  if (variant === 'faded' || computedColor.value === 'slate') {
    return 'secondary';
  }
  return 'default';
});

const isIconOnly = computed(() => !props.label && !slots.default);

const KIT_SIZE = { xs: 'xs', sm: 'sm', md: 'default', lg: 'lg' };
const KIT_ICON_SIZE = {
  xs: 'icon-xs',
  sm: 'icon-sm',
  md: 'icon',
  lg: 'icon-lg',
};
const JUSTIFY = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

const buttonClass = computed(() => {
  const size = (isIconOnly.value ? KIT_ICON_SIZE : KIT_SIZE)[
    computedSize.value
  ];
  const isRuby = computedColor.value === 'ruby';
  return cn(
    buttonVariants({ variant: kitVariant.value, size }),
    // The kit's base has `shrink-0`; the woot's layouts (dialog footers with two `w-full` buttons side by side)
    // were written for a button that shrinks, and with `shrink-0` they overflow their container.
    'min-w-0 shrink',
    kitVariant.value === 'link' && 'size-auto h-auto p-0 has-[>svg]:px-0',
    isRuby &&
      ['outline', 'ghost', 'link'].includes(kitVariant.value) &&
      'text-polaris-destructive hover:text-polaris-destructive',
    JUSTIFY[computedJustify.value],
    !props.noAnimation && 'active:enabled:scale-[0.98]',
    props.trailingIcon && !isIconOnly.value && 'flex-row-reverse',
    attrs.class
  );
});
</script>

<template>
  <button v-bind="filteredAttrs" :class="buttonClass">
    <slot v-if="(icon || $slots.icon) && !isLoading" name="icon">
      <Icon :icon="icon" class="flex-shrink-0" />
    </slot>

    <Spinner v-if="isLoading" class="!w-4 !h-4 flex-shrink-0" />

    <slot v-if="label || $slots.default" name="default">
      <span v-if="label" class="min-w-0 truncate">{{ label }}</span>
    </slot>
  </button>
</template>
